"use client";

import { XMarkIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import type { ReviewerFeedback, ReviewerSeverity } from '@/types/reviewer';

interface ReviewerDetailsDrawerProps {
  feedback: ReviewerFeedback | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestChanges?: () => void;
}

const severityConfig: Record<ReviewerSeverity, {
  icon: typeof InformationCircleIcon;
  color: string;
  bg: string;
}> = {
  info: {
    icon: InformationCircleIcon,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
  },
  critical: {
    icon: ExclamationCircleIcon,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
  },
};

export function ReviewerDetailsDrawer({
  feedback,
  isOpen,
  onClose,
  onRequestChanges,
}: ReviewerDetailsDrawerProps) {
  if (!isOpen || !feedback) return null;

  const severity = feedback.severity || 'info';
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-muted shadow-2xl z-50 flex flex-col transform transition-transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reviewer-drawer-title"
      >
        <div className="flex items-center justify-between p-4 border-b border-border dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${config.color}`} />
            <h2 id="reviewer-drawer-title" className="text-lg font-semibold text-foreground dark:text-gray-100">
              Reviewer Feedback
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted dark:hover:bg-gray-800 text-muted-foreground dark:text-muted-foreground transition-colors"
            aria-label="Close drawer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className={`p-3 rounded-lg ${config.bg} mb-4`}>
            <p className="text-sm font-medium text-foreground dark:text-gray-100">
              {feedback.summary}
            </p>
          </div>

          {feedback.bullets.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground dark:text-muted-foreground mb-2">
                Detailed Feedback
              </h3>
              <ul className="space-y-3">
                {feedback.bullets.map((bullet, idx) => (
                  <li
                    key={`${feedback.id}-drawer-${idx}`}
                    className="flex items-start gap-3 text-sm text-foreground dark:text-muted-foreground"
                  >
                    <span
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${config.bg} ${config.color} text-xs font-semibold`}
                    >
                      {idx + 1}
                    </span>
                    <span className="flex-1 pt-0.5">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-border dark:border-gray-800">
            <p className="text-xs text-muted-foreground dark:text-muted-foreground">
              Feedback ID: {feedback.id.substring(0, 8)}...
            </p>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">
              Created: {new Date(feedback.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-border dark:border-gray-800">
          {onRequestChanges && (
            <button
              onClick={() => {
                onRequestChanges();
                onClose();
              }}
              className="w-full py-3 px-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
            >
              Request Changes
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full mt-2 py-3 px-4 rounded-lg bg-muted text-foreground font-medium hover:bg-gray-200 dark:bg-muted dark:text-muted-foreground dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}
